import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import type { CategoryAttribute, ColorOption } from '../types/category';
import {
  resolveConditionalDependsOnValue,
  resolveConditionalSelectLabel,
  resolveConditionalSelectOptions,
} from '../utils/conditionalSelectAttribute';
import {
  findOtherSelectOption,
  getListedStringOptions,
  getOtherSpecifyFieldLabel,
  getSelectOtherState,
  isOtherSelectOption,
} from '../utils/selectOtherOption';

type Props = {
  attributeKey: string;
  attribute: CategoryAttribute;
  value: string;
  dependentValues: Record<string, string>;
  onChange: (value: string) => void;
};

export default function AdminCategoryAttributeField({
  attributeKey,
  attribute,
  value,
  dependentValues,
  onChange,
}: Props) {
  const [availableOptions, setAvailableOptions] = useState<Array<string | ColorOption>>([]);

  useEffect(() => {
    if (attribute.type === 'conditional_select') {
      setAvailableOptions(resolveConditionalSelectOptions(attributeKey, attribute, dependentValues));
    } else if (attribute.type === 'select' && Array.isArray(attribute.options)) {
      setAvailableOptions(attribute.options);
    } else if (attribute.type === 'color_select' && Array.isArray(attribute.options)) {
      setAvailableOptions(attribute.options);
    } else {
      setAvailableOptions([]);
    }
  }, [attribute, attributeKey, dependentValues]);

  const dependsOnValue =
    attribute.type === 'conditional_select'
      ? resolveConditionalDependsOnValue(attributeKey, attribute, dependentValues)
      : '';

  const displayLabel =
    attribute.type === 'conditional_select'
      ? resolveConditionalSelectLabel(attribute, dependsOnValue)
      : attribute.label;

  const isRequired = Boolean(attribute.required);

  if (attribute.type === 'text' || attribute.type === 'string') {
    return (
      <TextField
        fullWidth
        multiline={attribute.type === 'text'}
        minRows={attribute.type === 'text' ? 3 : undefined}
        label={attribute.label}
        required={isRequired}
        placeholder={attribute.placeholder}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        helperText={attribute.secret_detail ? 'Détail secret (visible admin uniquement)' : undefined}
      />
    );
  }

  if (attribute.type === 'date') {
    return (
      <TextField
        fullWidth
        type="date"
        label={attribute.label}
        required={isRequired}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  if (attribute.type === 'number') {
    return (
      <TextField
        fullWidth
        type="number"
        label={attribute.label}
        required={isRequired}
        placeholder={attribute.placeholder}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (attribute.type === 'boolean') {
    return (
      <FormControl fullWidth required={isRequired}>
        <InputLabel>{attribute.label}</InputLabel>
        <Select
          label={attribute.label}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <MenuItem value="">
            <em>Non spécifié</em>
          </MenuItem>
          <MenuItem value="true">Oui</MenuItem>
          <MenuItem value="false">Non</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (attribute.type === 'color_select') {
    const rawOptions = (Array.isArray(attribute.options) ? attribute.options : []) as unknown[];
    const colorOptions = rawOptions
      .filter(
        (option): option is ColorOption =>
          typeof option === 'object' && option !== null && typeof (option as ColorOption).label === 'string'
      );

    return (
      <FormControl fullWidth required={isRequired}>
        <InputLabel>{attribute.label}</InputLabel>
        <Select
          label={attribute.label}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <MenuItem value="">
            <em>Non spécifié</em>
          </MenuItem>
          {colorOptions.map((option) => (
            <MenuItem key={option.label} value={option.label}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {option.hex ? (
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: option.hex,
                      border: '1px solid',
                      borderColor: 'divider',
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                <span>{option.label}</span>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (attribute.type === 'select' || attribute.type === 'conditional_select') {
    const listedOptions = getListedStringOptions(availableOptions);
    const { selectValue, showOtherSpecify, otherSpecifyValue } = getSelectOtherState(
      value || '',
      listedOptions
    );

    const handleSelectChange = (nextValue: string) => {
      if (isOtherSelectOption(nextValue)) {
        onChange(nextValue);
        return;
      }
      onChange(nextValue);
    };

    const handleOtherSpecifyChange = (customValue: string) => {
      if (!customValue.trim()) {
        onChange(findOtherSelectOption(listedOptions) || 'Autre');
        return;
      }
      onChange(customValue);
    };

    return (
      <Box>
        <FormControl fullWidth required={isRequired}>
          <InputLabel>{displayLabel}</InputLabel>
          <Select
            value={selectValue}
            onChange={(e) => handleSelectChange(e.target.value)}
            label={displayLabel}
            disabled={availableOptions.length === 0}
          >
            <MenuItem value="">
              <em>Non spécifié</em>
            </MenuItem>
            {availableOptions.length === 0 ? (
              <MenuItem disabled>
                <em>Aucune option disponible</em>
              </MenuItem>
            ) : (
              availableOptions.map((option) => {
                if (typeof option === 'string') {
                  return (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  );
                }
                return null;
              })
            )}
          </Select>
          {attribute.type === 'conditional_select' && availableOptions.length === 0 ? (
            <FormHelperText>Sélectionnez d&apos;abord le champ parent.</FormHelperText>
          ) : null}
        </FormControl>
        {showOtherSpecify ? (
          <TextField
            fullWidth
            label={getOtherSpecifyFieldLabel(displayLabel)}
            required={isRequired}
            value={otherSpecifyValue}
            onChange={(e) => handleOtherSpecifyChange(e.target.value)}
            sx={{ mt: 1.5 }}
          />
        ) : null}
      </Box>
    );
  }

  return (
    <TextField
      fullWidth
      label={attribute.label}
      required={isRequired}
      placeholder={attribute.placeholder}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      helperText={attribute.secret_detail ? 'Détail secret' : undefined}
    />
  );
}
